import React from "react";
import { Box, Typography, Link } from "@mui/material";
import Layout from "../Util/Layout";
import RotationArc from "../../Assets/RotationARC.png"

const ARCAGIPage = () => {
    return (
        <Layout>
            <Box p={3}>
                <Typography variant="h3" gutterBottom>
                    ARC-AGI Solver
                </Typography>

                <Typography variant="h4" color="textSecondary" paragraph>
                    My Role: Solo Developer
                </Typography>

                {/* Project Description */}
                <Typography variant="h5" gutterBottom>
                    Project Overview
                </Typography>
                <Typography paragraph>
                    I developed an agent that is capable of generalizing and solving ARC-AGI problems. If you are unfamiliar with such problems, I
                    wrote up a quick article explaining the concept <Link href="https://www.nickcolleran.com/articles/arc-problems---overview" target="_blank" rel="noopener noreferrer">here</Link>.
                </Typography>

                <Box my={3}>
                    <img
                        src={RotationArc}
                        alt="Manual Strategy Logic Flow"
                        style={{
                            maxWidth: "100%",
                            height: "auto",
                            display: "block",
                            marginLeft: "auto",
                            marginRight: "auto",
                        }}
                    />
                </Box>

                <Typography variant="h5" gutterBottom>
                    Under the hood
                </Typography>
                <Typography paragraph>
                    The foundation of this agent is a deterministic way to solve all Color-Change-Only problems, which are instances that
                    only require shifting cell colors and maintaining the same grid pattern. I would love to discuss the details of this base-case
                    solution in private. From there, this is used as a base case, and a Domain-Specific Language (DSL) representing grid transformations
                    tries to reduce problems to this base case. I wrote about this process
                    <Link href="https://www.nickcolleran.com/articles/solving-arc-problems-with-a-dsl" target="_blank" rel="noopener noreferrer"> here</Link>.
                    Finally, any problems that are not solved by the DSL are sent off to a special case
                    handler that contains hand-crafted solutions to common problem types within the problem space.
                </Typography>

                <Typography variant="h5" gutterBottom>
                    Performance
                </Typography>
                <Typography paragraph>
                    This agent was tested against a private set of 96 cases, and is able to solve 74 examples consistently, or ~77%. It is worth
                    noting that this performance does not extend to the public ARC-AGI problem set, as that level of performance would be nearly
                    groundbreaking. The agent solves a handful of problems from the public set well, and I am working to improve performance before
                    I attempt to compete in the official challenge to see how far I can push this agent.
                </Typography>

                <Typography variant="h5" gutterBottom>
                    Technologies Used
                </Typography>
                <ul>
                    <li>
                        <Typography>
                            <strong>Python:</strong> This entire application was written in Python to take advantage of quick mathematical processing
                            libraries
                        </Typography>
                    </li>
                    <li>
                        <Typography>
                            <strong>NumPy:</strong> This project took me from intermediate to advanced with NumPy. I utilized NumPy for all grid processing,
                            larger-scale calculations, and data management.
                        </Typography>
                    </li>
                </ul>

                {/* What I Learned */}
                <Typography variant="h5" gutterBottom>
                    What I Learned
                </Typography>
                <Typography paragraph>
                    The biggest thing I learned in this project is that generalization is extremely difficult. ARC Problems
                    are designed to be very challenging for agents, so this project really made me think about how to apply human-like
                    thinking methodologies to an AI system rather than focusing strictly on traditional AI or ML approaches. I also learned
                    that getting a complete picture of the problem space is crucial, but nearly impossible for general cases. For future
                    AI or ML projects, I will absolutely spend much more time thoroughly exploring and documenting the problem space,
                    and specifically searching for edge cases that an agent might not know how to handle. Finally, I learned how to manage
                    continuous agent iteration. Agents need to be observed and fine-tuned over time, so looking for a "set it and forget it"
                    AI-based solution should not be my goal moving forward.
                </Typography>

            </Box>
        </Layout>
    );
};

export default ARCAGIPage;
